import React, { useEffect, useState, useCallback } from 'react'
import axios from 'axios'
import { FileText, Globe, Database, Boxes, Cloud, Plug, RefreshCw, ExternalLink } from 'lucide-react'
import { getSession } from '../lib/session'

// "Your data" — surfaces what the platform has actually indexed from your sources,
// so a connected source shows up as browsable content ("here's what I found").
const TYPE_ICON = { url: Globe, sitemap: Globe, text: FileText, file: FileText, rag: Boxes, sql: Database, s3: Cloud, gcs: Cloud, azure: Cloud, confluence: Plug, jira: Plug, monday: Plug, asana: Plug, salesforce: Plug, sap: Plug, slack: Plug }
const TYPE_LABEL = { url: 'Website', sitemap: 'Sitemap', text: 'Text', file: 'File', rag: 'RAG', sql: 'SQL', s3: 'S3', gcs: 'GCS', azure: 'Azure', confluence: 'Confluence', jira: 'Jira', monday: 'monday', asana: 'Asana', salesforce: 'Salesforce', sap: 'SAP', slack: 'Slack' }
const ago = (d) => { if (!d) return ''; const s = Math.floor((Date.now() - new Date(d)) / 1000); if (s < 90) return 'just now'; if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago` }

export default function KnowledgeView({ compact = false }) {
  const ns = getSession()?.namespaceId
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(false)
  const load = useCallback(() => {
    if (!ns) return
    setBusy(true)
    axios.get(`/api/v1/tenants/${ns}/documents`).then(r => setData(r.data)).catch(() => setData({ summary: { docs: 0, passages: 0 }, byType: [], documents: [] })).finally(() => setBusy(false))
  }, [ns])
  useEffect(load, [load])

  const s = data?.summary || {}
  const docs = data?.documents || []

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><FileText size={18} className="text-primary-600" /> Your data</h3>
        <button onClick={load} className="text-sm text-gray-400 hover:text-gray-700 flex items-center gap-1"><RefreshCw size={14} className={busy ? 'animate-spin' : ''} /> Refresh</button>
      </div>
      <p className="text-sm text-gray-500 mb-4">What the platform has indexed from your sources — shared across every service on this workspace.</p>

      {/* summary */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4">
        <div><span className="text-2xl font-bold text-gray-900 tabular-nums">{(s.docs ?? 0).toLocaleString()}</span> <span className="text-sm text-gray-500">documents</span></div>
        <div><span className="text-2xl font-bold text-gray-900 tabular-nums">{(s.passages ?? 0).toLocaleString()}</span> <span className="text-sm text-gray-500">passages</span></div>
        {s.latest && <div className="text-xs text-gray-400">last added {ago(s.latest)}</div>}
        <div className="flex flex-wrap gap-1.5 ml-auto">
          {(data?.byType || []).map(t => (
            <span key={t.source_type} className="text-[11px] bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">{TYPE_LABEL[t.source_type] || t.source_type}: {t.c}</span>
          ))}
        </div>
      </div>

      {/* list */}
      {docs.length === 0 ? (
        <div className="text-sm text-gray-500 border-t border-gray-100 pt-4">
          No content indexed yet. Connect a source — from <b>Instances → Add a source</b> — and it’ll appear here within a minute or two.
        </div>
      ) : (
        <div className="border-t border-gray-100 divide-y divide-gray-100 max-h-96 overflow-y-auto">
          {(compact ? docs.slice(0, 6) : docs).map((d) => {
            const Icon = TYPE_ICON[d.source_type] || FileText
            return (
              <div key={d.id} className="flex items-center gap-3 py-2.5">
                <div className="p-1.5 rounded-lg bg-gray-100 shrink-0"><Icon size={15} className="text-gray-500" /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{d.title || '(untitled)'}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1.5">
                    <span className="uppercase tracking-wide text-[10px] bg-gray-100 rounded px-1 py-0.5">{TYPE_LABEL[d.source_type] || d.source_type}</span>
                    {d.chunk_count ? `${d.chunk_count} passages` : <span className="text-amber-600">⚠ 0 passages (not indexed)</span>} · added {ago(d.created_at)}
                    {d.source_url && <a href={d.source_url} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline inline-flex items-center gap-0.5">source <ExternalLink size={11} /></a>}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
